import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import FileLocation from '../components/FileLocation';
import { useNavigate, NavLink } from 'react-router-dom';
import { DocumentCheckIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import HeadingCp from '../components/HeadingCp';
import LoadingSpinner from '../components/LoadingSpinner';
import CurrencyFormat from '../components/CurrencyFormat';
import WipComments from '../components/ModalWipNotesSection';
import { useUserData } from '../context/UserData';

const JobClosedStatPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiReport = `${apiUrl}/report_crud.php`;
  const apiWip = `${apiUrl}/wip_crud.php`;
  const navigate = useNavigate();
  const userData = useUserData();

  const [loading, setLoading] = useState(false);
  const [jobNumber, setJobNumber] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [wipId, setWipId] = useState(null);

  const [notes, setNotes] = useState([]);
  const [bodyDataNotes, setBodyDataNotes] = useState({
    status: 'submit_notes',
    data: {},
  });

  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobSearchResults, setJobSearchResults] = useState([]);
  const [isJobDropdownVisible, setIsJobDropdownVisible] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const [isGlobalLaborModalOpen, setIsGlobalLaborModalOpen] = useState(false);
  const [globalLaborForm, setGlobalLaborForm] = useState({
    labor_price: '',
  });  
  const [isLaborModalOpen, setIsLaborModalOpen] = useState(false); 
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    job_number: '',
    overide_labor: '',
    overide_material: '',
    add_on: '',
    overide_co_cost: '',
    override_labor_price: '',
  });  

  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);  

  const handleNoteChangeParent = (id, newNote) => {
    setNotes((prev) => [...(Array.isArray(prev) ? prev : []), newNote]);

    setSelectedJob((prev) => ({
      ...prev,
      notes: [...(Array.isArray(prev?.notes) ? prev.notes : []), newNote],
    }));
  };

  const handleJobSearchChange = (e) => {
    const value = e.target.value;

    setJobSearchQuery(value);
    setJobNumber('');
    setWipId(null);
    setSelectedJob(null);
    setNotes([]);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (value.trim().length >= 3) {
      const timeout = setTimeout(async () => {
        try {
          const response = await axios.get(`${apiUrl}/wip_crud.php`, {
            params: { getSchedulerJobSites: value },
          });

          const results = Array.isArray(response.data) ? response.data : [];
          setJobSearchResults(results);
          setIsJobDropdownVisible(true);
        } catch (error) {
          setJobSearchResults([]);
          setIsJobDropdownVisible(false);
          toast.error('Error searching jobs.', { position: 'top-center' });
        }
      }, 400);

      setDebounceTimeout(timeout);
    } else {
      setJobSearchResults([]);
      setIsJobDropdownVisible(false);
    }
  };

  const handleJobSelect = (job) => {
    setWipId(job.id);
    setJobNumber(job.job_number || '');
    setJobSearchQuery(`${job.job_number} - ${job.job_site}`);
    setJobSearchResults([]);
    setIsJobDropdownVisible(false);
  };

  const fetchJobClosedStat = async () => {
    if (!wipId) {
      toast.info('Select a job first.', { position: 'top-center' });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(apiReport, {
        params: {
          getWipDetailStat: wipId,
        },
      });
      if (response.data?.wip_detail) {
        const detail = response.data.wip_detail;
        const responseNotes = Array.isArray(response.data.notes) ? response.data.notes : [];

        setNotes(responseNotes);

        setBodyDataNotes({
          status: 'submit_notes',
          data: {},
        });

        const proposalRows = Array.isArray(response.data.proposal_stats?.stats)
          ? response.data.proposal_stats.stats
          : [];

        const changeOrderRows = proposalRows.filter((row) =>
          String(row.jobnum || '').toUpperCase().includes('CO')
        );

        const changeOrdersTotal = changeOrderRows.reduce(
          (sum, row) => sum + Number(row.price || 0),
          0
        );        

        setSelectedJob({
          ...detail,
          original_proposal: detail.price || 0,
          global_labor_price: response.data.project_global_settings?.labor_price || 0,

          changeorders: changeOrderRows,
          changeorders_total: changeOrdersTotal,
          proposal_rows: proposalRows,

          total_labor: response.data.project_stats?.labor_total || 0,
          total_materials: response.data.project_stats?.material_total || 0,
          total_extras: Array.isArray(response.data.account_vendor) ? response.data.account_vendor.reduce(
                (sum, vendor) => sum + Number(vendor.total_price || 0), 0 ) : 0,

          first_invoice_date: response.data.invoice_history?.[0]?.date || null,
          final_inspection_date: response.data.engineering_data?.final_inspection || null,
          account_vendor: response.data.account_vendor || [],
          engineering_data: response.data.engineering_data || null,
          hours_per_tech: response.data.hours_per_tech || [],
          purchase_orders: response.data.purchase_orders || [],
          notes: responseNotes,
          cost_items: [
            { label: 'FSM', checked: true },
            { label: 'calendar', checked: true },
            { label: 'timesheets', checked: (response.data.hours_per_tech || []).length > 0 },
            { label: 'vendors', checked: (response.data.account_vendor || []).length > 0 },
            { label: 'Darin', checked: true },
            { label: 'engineers', checked: !!response.data.engineering_data },
            { label: 'UPS', checked: (response.data.purchase_orders?.[1] || []).length > 0 },
          ],
        });

      } else {
        setSelectedJob(null);
        setNotes([]);
        toast.error(response.data?.error || 'No job data found.', {
          position: 'top-center',
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } 
      else if (error.response?.status === 403) {
        navigate('/login');
        toast.error('Not authorized.', { position: 'top-center' });
      } 
      else {
        toast.error('Server error loading job stats.', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  if (!userData?.users) return; // wait until user loads

  const allowedTitles = [
    'CFO',
    'Director of Operations',
    'President',
    'CEO'
  ];

  if (!allowedTitles.includes(userData.users.title)) {
    navigate('/');
    toast.error('Not authorized.', { position: 'top-center' });
    return;
  }

  if (wipId) {
    fetchJobClosedStat();
  }

}, [userData, wipId]);
    
    // if (wipId) {
    //   fetchJobClosedStat();
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    if (!selectedJob) {
      return {
        totalJobSell: 0,
        totalJobCost: 0,
        profitAmount: 0,
        profitPercentage: 0,
        totalLabor: 0,
        laborHours: 0,
        overtimeHours: 0,
        laborRate: 0,
      };
    }

    const toNum = (val) => {
      if (val === null || val === undefined || val === '') return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    };

    // ✅ FIX: declare this
    const originalProposal = Number(selectedJob.original_proposal || 0);

    // ✅ FIX: ONLY ONE changeOrdersTotal
    const changeOrdersTotal =
      toNum(selectedJob.overide_co_cost) ??
      Number(selectedJob.changeorders_total || 0);

    // ===== LABOR LOGIC =====
    const hoursPerTech = Array.isArray(selectedJob.hours_per_tech)
      ? selectedJob.hours_per_tech
      : [];

    const laborSummary = hoursPerTech.find((item) => !item.tech_id) || {};

    const liveRegularHours = Number(laborSummary.total_reg_hrs || 0);
    const liveOvertimeHours = Number(laborSummary.total_over_hrs || 0);

    const laborHours =
      toNum(selectedJob.overide_labor) ??
      liveRegularHours;

    const overtimeHours = liveOvertimeHours;

    const laborRate =
      toNum(selectedJob.override_labor_price) ??
      toNum(selectedJob.global_labor_price) ??
      0;

    const totalLabor =
      (laborHours * laborRate) + ((overtimeHours * 1.5) * laborRate);

    // ===== MATERIAL / EXTRAS =====
    const totalMaterials =
      toNum(selectedJob.overide_material) ??
      Number(selectedJob.total_materials || 0);

    const totalExtras =
      toNum(selectedJob.add_on) ??
      Number(selectedJob.total_extras || 0);

    // ===== FINAL CALC =====
    const totalJobSell = originalProposal + changeOrdersTotal;
    const totalJobCost = totalLabor + totalMaterials + totalExtras;

    const profitAmount = totalJobSell - totalJobCost;

    const profitPercentage =
      totalJobSell > 0
        ? Math.round((profitAmount / totalJobSell) * 100)
        : 0;

    return {
      totalJobSell,
      totalJobCost,
      profitAmount,
      profitPercentage,
      totalLabor,
      laborHours,
      overtimeHours,
      laborRate,
      totalMaterials,
      totalExtras,
      changeOrdersTotal,
    };
  }, [selectedJob]);


  const saveOverride = async () => {
  try {
    const response = await axios.post(
      apiReport,
      {
        status: 'save_project_stat_override',
        data: overrideForm,
      },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (response.data?.success) {
      toast.success('Override saved!', { position: 'top-center' });
      setIsOverrideModalOpen(false);
      fetchJobClosedStat();
    } else {
      toast.error(response.data?.error || 'Failed to save override.', {
        position: 'top-center',
      });
    }
  } catch (error) {
    toast.error('Server error saving override.', { position: 'top-center' });
  }
  };

  const openOverrideModal = () => {
    if (!selectedJob?.job_number) {
      toast.info('Select a job first.', { position: 'top-center' });
      return;
    }

    setOverrideForm({
      job_number: selectedJob.job_number,
      overide_labor: selectedJob.overide_labor ?? '',
      overide_material: selectedJob.overide_material ?? '',
      add_on: selectedJob.add_on ?? '',
      overide_co_cost: selectedJob.overide_co_cost ?? '',
      override_labor_price: selectedJob.override_labor_price ?? '',
    });

    setIsOverrideModalOpen(true);
  }; 

  const openGlobalLaborModal = () => {
    setGlobalLaborForm({
      labor_price: selectedJob?.global_labor_price || '',
    });

    setIsGlobalLaborModalOpen(true);
  }; 

  const saveGlobalLaborPrice = async () => {
    try {
      const response = await axios.post(
        apiReport,
        {
          status: 'save_global_labor_price',
          data: globalLaborForm,
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.data?.success) {
        toast.success('Global labor price saved!', { position: 'top-center' });
        setIsGlobalLaborModalOpen(false);

        if (selectedJob) {
          fetchJobClosedStat();
        }
      } else {
        toast.error(response.data?.error || 'Failed to save global labor price.', {
          position: 'top-center',
        });
      }
    } catch (error) {
      toast.error('Server error saving global labor price.', {
        position: 'top-center',
      });
    }
  };  

  return (
    <div className="max-w-[1400px] mx-auto p-4">
      <HeadingCp label="Job Closed Stat" />

      <div className="rounded-xl border bg-white p-4 shadow-sm mb-6">
        <label className="block text-sm text-gray-700 mb-1">
          Search Job
        </label>

        <div className="relative">
          <input
            type="text"
            value={jobSearchQuery}
            onBlur={() => {
              setTimeout(() => setIsJobDropdownVisible(false), 150);
            }}
            onChange={handleJobSearchChange}
            placeholder="Search by job number or address..."
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            autoComplete="off"
          />

          {jobSearchQuery && (
            <button
              type="button"
              onClick={() => {
                setJobSearchQuery('');
                setJobNumber('');
                setWipId(null);
                setSelectedJob(null);
                setJobSearchResults([]);
                setNotes([]);
              }}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-700"
            >
              ✕
            </button>
          )}

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={openOverrideModal}
            disabled={!selectedJob}
            className={`px-4 py-2 rounded-md text-sm font-semibold text-white ${
              selectedJob
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Override
          </button>

          <button
            type="button"
            onClick={openGlobalLaborModal}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700">
            Global Labor Price
          </button>
          <button
            type="button"
            onClick={() => navigate(`/wip_detail/${selectedJob?.id}`)}
            disabled={!selectedJob?.id}
            className={`px-4 py-2 rounded-md text-sm font-semibold text-white ${
              selectedJob?.id
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Full Detail
          </button>
          {/* <button
            type="button"
            onClick={() => window.print()}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-gray-800 hover:bg-gray-900"
          >
            Print
          </button>           */}
        </div>        

        {jobNumber && (
          <p className="mt-2 text-sm text-green-700">
            Selected: {jobSearchQuery}
          </p>
        )}

        {isJobDropdownVisible && (
          <ul className="absolute z-20 mt-1 w-full bg-white border rounded shadow-lg max-h-72 overflow-auto divide-y">
            {jobSearchResults.length > 0 ? (
              jobSearchResults.map((job) => (
                <li
                  key={`${job.job_number}-${job.job_site}`}
                  onClick={() => handleJobSelect(job)}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                >
                  <div className="text-sm font-semibold text-green-700">
                    Job#: {job.job_number}
                  </div>

                  <div className="text-sm text-blue-900">
                    {job.customer_name} | {job.f_p_type} | {job.type}
                  </div>

                  <div className="text-sm font-medium text-gray-800">
                    {job.job_site}
                  </div>
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500">
                No jobs found
              </li>
            )}
          </ul>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          {loading ? (
            <LoadingSpinner>Loading job stats...</LoadingSpinner>
          ) : selectedJob ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Total Job Sell</p>
                  <p className="mt-1 text-2xl font-bold text-red-600">
                    <CurrencyFormat value={stats.totalJobSell} />
                  </p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Total Job Cost</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    <CurrencyFormat value={stats.totalJobCost} />
                  </p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Profit Amount</p>
                  <p className="mt-1 text-2xl font-bold text-green-700">
                    <CurrencyFormat value={stats.profitAmount} />
                  </p>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Profit Percentage</p>
                  <p className="mt-1 text-2xl font-bold text-blue-700">
                    {stats.profitPercentage}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
                  <div className="bg-blue-300 text-center py-2 font-bold">
                    Job Costing Spreadsheet
                  </div>

                  <table className="min-w-full border-collapse">
                    <tbody>
                      <tr>
                        <td className="border px-3 py-2 font-medium">Customer:</td>
                        <td className="border px-3 py-2 text-center">
                          {selectedJob.customer_name}
                        </td>
                      </tr>

                      <tr>
                        <td className="border px-3 py-2 font-medium">Job:</td>
                        <td className="border px-3 py-2 text-center">
                          {selectedJob.job_site}
                        </td>
                      </tr>

                      <tr className="bg-blue-50">
                        <td className="border px-3 py-2 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">
                            Original Proposal
                          </span>

                          <FileLocation
                            expFile={selectedJob.file}
                            where={'signed_proposal'}
                          />
                        </div>
                        </td>
                        <td className="border px-3 py-2 text-center">
                          <CurrencyFormat value={selectedJob.original_proposal} /> 
                        </td>
                      </tr>

                      <tr className="bg-blue-50">
                        <td className="border px-3 py-2 font-medium">Change Orders</td>
                        <td className="border px-3 py-2 text-center">
                          <CurrencyFormat value={selectedJob.changeorders_total} />
                        </td>
                      </tr>

                      <tr className="bg-blue-100">
                        <td className="border px-3 py-2 font-bold">Total Job Sell</td>
                        <td className="border px-3 py-2 text-center font-bold text-red-600">
                          <CurrencyFormat value={stats.totalJobSell} />
                        </td>
                      </tr>

                      <tr className="bg-blue-100">
                        <td className="border px-3 py-2 font-medium">
                          <button
                            type="button"
                            onClick={() => setIsLaborModalOpen(true)}
                            className="flex items-center gap-2 text-blue-700 font-semibold hover:underline"
                          >
                            Total Labor
                            <span className="text-xs text-gray-500">{'>>'}</span>
                          </button>
                        </td>

                        <td className="border px-3 py-2 text-center">
                          <CurrencyFormat value={stats.totalLabor} />

                          <div className="text-xs text-gray-500">
                            {stats.laborHours} reg hrs + {stats.overtimeHours} OT hrs ×{' '}
                            <CurrencyFormat value={stats.laborRate} />
                          </div>
                        </td>
                      </tr>

                      <tr className="bg-blue-100">
                        <td className="border px-3 py-2 font-medium">Total Materials</td>
                        <td className="border px-3 py-2 text-center">
                          <CurrencyFormat value={stats.totalMaterials} />
                        </td>
                      </tr>

                      <tr className="bg-blue-100">
                        <td className="border px-3 py-2 font-medium">
                          <button
                            type="button"
                            onClick={() => setIsVendorModalOpen(true)}
                            className="flex items-center gap-2 text-blue-700 font-semibold hover:underline"
                          >
                            Total Extras
                            <span className="text-xs text-gray-500">{'>>'}</span>
                          </button>
                        </td>

                        <td className="border px-3 py-2 text-center">
                          <CurrencyFormat value={stats.totalExtras} />

                          {selectedJob.add_on !== null && selectedJob.add_on !== '' && (
                            <div className="text-xs text-orange-600 font-medium">
                              Override
                            </div>
                          )}
                        </td>
                      </tr>

                      <tr className="bg-blue-200">
                        <td className="border px-3 py-2 font-bold">Total Job Cost</td>
                        <td className="border px-3 py-2 text-center font-bold">
                          <CurrencyFormat value={stats.totalJobCost} />
                        </td>
                      </tr>

                      <tr className="bg-blue-300">
                        <td className="border px-3 py-2 font-bold">Profit Amount</td>
                        <td className="border px-3 py-2 text-center font-bold">
                          <CurrencyFormat value={stats.profitAmount} />
                        </td>
                      </tr>

                      <tr className="bg-blue-300">
                        <td className="border px-3 py-2 font-bold">Profit Percentage</td>
                        <td className="border px-3 py-2 text-center font-bold">
                          {stats.profitPercentage}%
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="p-4 space-y-2 text-sm">
                    <p>
                      <span className="font-medium">1st invoice:</span>{' '}
                      {selectedJob.first_invoice_date || 'N/A'}
                    </p>

                    <p>
                      <span className="font-medium">Final inspection:</span>{' '}
                      {selectedJob.final_inspection_date || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-xl border bg-white p-4 shadow-sm">
                    <h2 className="font-semibold text-gray-800 mb-3">
                      Cost Items To Check
                    </h2>

                    <table className="min-w-full border-collapse">
                      <tbody>
                        {(selectedJob.cost_items || []).map((item) => (
                          <tr key={item.label}>
                            <td className="border px-3 py-2 text-center w-12">
                              {item.checked ? 'x' : ''}
                            </td>
                            <td className="border px-3 py-2">{item.label}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
                    <div className="text-center py-2 font-bold border-b">
                      Change Orders
                    </div>

                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border px-3 py-2 text-left text-sm">Job #</th>
                          <th className="border px-3 py-2 text-left text-sm">Description</th>
                          <th className="border px-3 py-2 text-right text-sm">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(selectedJob.changeorders || []).map((co) => (
                          <tr key={co.id || co.jobnum}>
                            <td className="border px-3 py-2 text-sm">
                              {co.jobnum}
                            </td>

                            <td className="border px-3 py-2 text-sm whitespace-pre-wrap">
                              {co.description}
                            </td>

                            <td className="border px-3 py-2 text-sm text-right">
                              <CurrencyFormat value={co.price} />
                            </td>
                          </tr>
                        ))}

                        <tr>
                          <td className="border px-3 py-2 font-bold text-right" colSpan="2">
                            Total
                          </td>

                          <td className="border px-3 py-2 text-right font-bold">
                            <CurrencyFormat value={selectedJob.changeorders_total} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-xl border bg-white p-8 text-center text-gray-500 shadow-sm">
              Select a job to view closed job stats.
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold leading-6 text-gray-900 mb-4">
            Activity
          </h2>

          {selectedJob?.id ? (
            <WipComments
              api={apiWip}
              inside="notes"
              notes={notes}
              bodyData={bodyDataNotes}
              val=""
              id={selectedJob.id}
              handleNoteChangeParent={handleNoteChangeParent}
            />
          ) : (
            <p className="text-sm text-gray-500">
              Select a job to view activity.
            </p>
          )}
        </div>
      </div>
        {isOverrideModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Override Project Stat
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Job Number
                  </label>
                  <input
                    type="text"
                    value={overrideForm.job_number}
                    disabled
                    className="mt-1 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Override Labor Hrs
                  </label>
                  <input
                    type="number"
                    value={overrideForm.overide_labor}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({
                        ...prev,
                        overide_labor: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Override Material
                  </label>
                  <input
                    type="number"
                    value={overrideForm.overide_material}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({
                        ...prev,
                        overide_material: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Add On / Extras
                  </label>
                  <input
                    type="number"
                    value={overrideForm.add_on}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({
                        ...prev,
                        add_on: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Override Change Order Cost
                  </label>
                  <input
                    type="number"
                    value={overrideForm.overide_co_cost}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({
                        ...prev,
                        overide_co_cost: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mt-4">
                  Override Labor Price
                </label>
                <input
                  type="number"
                  value={overrideForm.override_labor_price}
                  onChange={(e) =>
                    setOverrideForm((prev) => ({
                      ...prev,
                      override_labor_price: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>              

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsOverrideModalOpen(false)}
                  className="px-4 py-2 rounded-md border text-sm"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={saveOverride}
                  className="px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}   

        {isGlobalLaborModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Global Labor Price
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Labor Price Per Hour
                </label>
                <input
                  type="number"
                  value={globalLaborForm.labor_price}
                  onChange={(e) =>
                    setGlobalLaborForm((prev) => ({
                      ...prev,
                      labor_price: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Example: 85"
                />
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsGlobalLaborModalOpen(false)}
                  className="px-4 py-2 rounded-md border text-sm"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={saveGlobalLaborPrice}
                  disabled={!globalLaborForm.labor_price}
                  className={`px-4 py-2 rounded-md text-white text-sm ${
                    globalLaborForm.labor_price
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}     

        {isLaborModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Labor Details
                </h2>

                <button
                  type="button"
                  onClick={() => setIsLaborModalOpen(false)}
                  className="text-gray-500 hover:text-gray-800"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
                <p>
                  Labor Rate:{' '}
                  <span className="font-semibold">
                    <CurrencyFormat value={stats.laborRate} />
                  </span>
                </p>
                <p>
                  Formula:{' '}
                  <span className="font-semibold">
                    Regular Hours × Rate + Overtime Hours × 1.5 × Rate
                  </span>
                </p>
              </div>

              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left text-sm">Tech</th>
                    <th className="border px-3 py-2 text-right text-sm">Regular Hours</th>
                    <th className="border px-3 py-2 text-right text-sm">Overtime Hours</th>
                    <th className="border px-3 py-2 text-right text-sm">Labor Cost</th>
                  </tr>
                </thead>

                <tbody>
                  {(selectedJob.hours_per_tech || [])
                    .filter((row) => row.tech_id)
                    .map((row) => {
                      const reg = Number(row.total_reg_hrs || 0);
                      const ot = Number(row.total_over_hrs || 0);
                      const cost = (reg * stats.laborRate) + ((ot * 1.5) * stats.laborRate);

                      return (
                        <tr key={row.tech_id}>
                          <td className="border px-3 py-2 text-sm">
                            {row.tech_name}
                          </td>
                          <td className="border px-3 py-2 text-sm text-right">
                            {reg}
                          </td>
                          <td className="border px-3 py-2 text-sm text-right">
                            {ot}
                          </td>
                          <td className="border px-3 py-2 text-sm text-right font-semibold">
                            <CurrencyFormat value={cost} />
                          </td>
                        </tr>
                      );
                    })}

                  <tr className="bg-blue-50 font-bold">
                    <td className="border px-3 py-2 text-sm text-right">
                      Total
                    </td>
                    <td className="border px-3 py-2 text-sm text-right">
                      {stats.laborHours}
                    </td>
                    <td className="border px-3 py-2 text-sm text-right">
                      {stats.overtimeHours}
                    </td>
                    <td className="border px-3 py-2 text-sm text-right">
                      <CurrencyFormat value={stats.totalLabor} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}  

        {isVendorModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-4xl rounded-xl bg-white p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Vendor / Extras Details
                </h2>

                <button
                  type="button"
                  onClick={() => setIsVendorModalOpen(false)}
                  className="text-gray-500 hover:text-gray-800"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
                <p>
                  Total Vendor Extras:{' '}
                  <span className="font-semibold">
                    <CurrencyFormat value={selectedJob.total_extras || 0} />
                  </span>
                </p>

                {selectedJob.add_on !== null && selectedJob.add_on !== '' && (
                  <p className="text-orange-700">
                    Override Total Used:{' '}
                    <span className="font-semibold">
                      <CurrencyFormat value={stats.totalExtras} />
                    </span>
                  </p>
                )}
              </div>

              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left text-sm">Job #</th>
                    <th className="border px-3 py-2 text-left text-sm">Vendor</th>
                    <th className="border px-3 py-2 text-left text-sm">Invoice #</th>
                    <th className="border px-3 py-2 text-left text-sm">Date</th>
                    <th className="border px-3 py-2 text-left text-sm">Status</th>
                    <th className="border px-3 py-2 text-right text-sm">Total</th>
                  </tr>
                </thead>

                <tbody>
                  {(selectedJob.account_vendor || []).map((vendor) => (
                    <tr key={vendor.id}>
                      <td className="border px-3 py-2 text-sm">
                        {vendor.job_number}
                      </td>

                      <td className="border px-3 py-2 text-sm">
                        {vendor.vendor_name}
                      </td>

                      <td className="border px-3 py-2 text-sm">
                        {vendor.adv_inv_num || 'N/A'}
                      </td>

                      <td className="border px-3 py-2 text-sm">
                        {vendor.date || 'N/A'}
                      </td>

                      <td className="border px-3 py-2 text-sm">
                        {vendor.status || 'N/A'}
                      </td>

                      <td className="border px-3 py-2 text-sm text-right font-semibold">
                        <CurrencyFormat value={vendor.total_price || 0} />
                      </td>
                    </tr>
                  ))}

                  <tr className="bg-blue-50 font-bold">
                    <td className="border px-3 py-2 text-sm text-right" colSpan="5">
                      Total
                    </td>
                    <td className="border px-3 py-2 text-sm text-right">
                      <CurrencyFormat value={selectedJob.total_extras || 0} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
};

export default JobClosedStatPage;